/**
 * frontend/src/sdk/hooks/use-cross-market.ts
 *
 * Typed TanStack Query hooks for cross-market arbitrage and correlation analysis.
 */

import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";
import type { CrossMarketCorrelation, CrossMarketOpportunity } from "@/sdk/types/data";

// =============================================================================
// CROSS-MARKET OPPORTUNITIES
// Backend: GET /api/cross-market/compare returns { total_matches, matches:
//          [_format_match] } (backend/routers/cross_market.py) — there is no
//          "opportunities" key, and "arbitrage_opportunities" on /compare is a
//          COUNT, not a list.  /api/cross-market/opportunities does not exist.
//          Matches are mapped to CrossMarketOpportunity below; the *_pct
//          fields are already percent units (backend multiplies by 100).
// =============================================================================

/** Wire shape of one prices[] entry inside a backend _format_match object. */
interface CrossMarketMatchPrice {
  source?: string;
  market_id?: string;
  market_name?: string;
  yes_price?: number;
  yes_price_pct?: number;
  no_price?: number;
}

/** Wire shape of a backend _format_match object (cross_market.py). */
interface CrossMarketMatch {
  match_id?: string;
  primary_question?: string;
  match_score?: number;
  price_range_pct?: number;
  arbitrage_opportunity?: boolean;
  arbitrage_profit_pct?: number;
  net_arbitrage_profit_pct?: number | null;
  prices?: CrossMarketMatchPrice[];
}

interface CrossMarketCompareResponse {
  success?: boolean;
  timestamp?: string;
  total_matches?: number;
  returned_matches?: number;
  matches?: CrossMarketMatch[];
}

function matchToOpportunity(
  match: CrossMarketMatch,
  detectedAt: string,
): CrossMarketOpportunity {
  const [venueA, venueB] = match.prices ?? [];
  return {
    market_a_id: venueA?.market_id ?? "",
    market_a_source: venueA?.source ?? "unknown",
    market_b_id: venueB?.market_id ?? "",
    market_b_source: venueB?.source ?? "unknown",
    question: match.primary_question ?? null,
    // NaN (not 0) when a venue leg is absent — renderers show "n/a", never a
    // fabricated price.
    price_a: typeof venueA?.yes_price === "number" ? venueA.yes_price : Number.NaN,
    price_b: typeof venueB?.yes_price === "number" ? venueB.yes_price : Number.NaN,
    spread_pct: typeof match.price_range_pct === "number" ? match.price_range_pct : Number.NaN,
    opportunity_type: match.arbitrage_opportunity ? "arbitrage" : "divergence",
    detected_at: detectedAt,
    estimated_edge_pct: match.net_arbitrage_profit_pct ?? null,
  };
}

export function useCrossMarketOpportunities(limit = 20) {
  return useQuery({
    queryKey: ["cross-market", "opportunities", limit],
    queryFn: async () => {
      const resp = await apiGet<CrossMarketCompareResponse>(
        `/api/cross-market/compare?limit=${limit}`,
      );
      const matches = resp.matches ?? [];
      return {
        opportunities: matches.map((match) => matchToOpportunity(match, resp.timestamp ?? "")),
        total: resp.total_matches ?? matches.length,
      };
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

// =============================================================================
// CROSS-MARKET CORRELATIONS
// Backend: GET /api/correlation/graph (backend/routers/correlation.py) is the
//          real correlation source — persisted correlation edges with market
//          ids, strength, direction, and type.  /api/cross-market/compare
//          carries NO correlation fields and ignores market_id, so the graph
//          is fetched whole and scoped to the market client-side.
// =============================================================================

interface CorrelationGraphEdge {
  source: string;
  target: string;
  type: string;
  strength: number;
  direction: number | null;
  reasoning?: string | null;
}

interface CorrelationGraphResponse {
  edges?: CorrelationGraphEdge[];
  n_edges?: number;
}

export function useCrossMarketCorrelations(marketId: string, enabled = true) {
  return useQuery({
    queryKey: ["cross-market", "correlations", marketId],
    queryFn: async () => {
      const resp = await apiGet<CorrelationGraphResponse>("/api/correlation/graph");
      const edges = (resp.edges ?? []).filter(
        (edge) => edge.source === marketId || edge.target === marketId,
      );
      const correlations: CrossMarketCorrelation[] = edges.map((edge) => ({
        market_id: edge.source,
        related_market_id: edge.target,
        // direction is +1/-1 (sign); strength is 0-1 magnitude.
        correlation: edge.strength * (edge.direction ?? 1),
        relationship: edge.type,
      }));
      return { correlations, total: correlations.length };
    },
    enabled: enabled && Boolean(marketId),
    staleTime: 120_000,
  });
}

// =============================================================================
// ARBITRAGE SCANNER
// Backend: GET  /api/arbitrage/scan   — scan for opportunities (legacy)
//          GET  /api/arbitrage/v2/opportunities — cached results
//          POST is not available; v2 GET is the correct read endpoint.
// =============================================================================

export function useArbitrageOpportunities(limit = 20) {
  return useQuery({
    queryKey: ["cross-market", "arbitrage", limit],
    queryFn: () =>
      apiGet<{ opportunities: CrossMarketOpportunity[]; total: number }>(
        `/api/arbitrage/v2/opportunities?limit=${limit}`,
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useArbitrageScanResults(params?: { min_edge_pct?: number; limit?: number }) {
  const search = new URLSearchParams();
  if (typeof params?.min_edge_pct === "number") {
    search.set("min_profit_pct", String(params.min_edge_pct));
  }
  if (typeof params?.limit === "number") {
    search.set("limit", String(params.limit));
  }
  const qs = search.toString();
  return useQuery({
    queryKey: ["arbitrage", "scan", params],
    // GET /api/arbitrage/scan returns pre-computed opportunities (read-only)
    queryFn: () =>
      apiGet<{ opportunities: CrossMarketOpportunity[]; scanned: number }>(
        `/api/arbitrage/scan${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 60_000,
  });
}

// =============================================================================
// POLYMARKET vs OTHERS
// =============================================================================

export function usePolymarketVsKalshi(limit = 10) {
  return useQuery({
    queryKey: ["cross-market", "poly-vs-kalshi", limit],
    queryFn: () =>
      apiGet<{ comparisons: CrossMarketCorrelation[]; total: number }>(
        `/api/cross-market/poly-vs-kalshi?limit=${limit}`,
      ),
    staleTime: 120_000,
  });
}
